<template>
    <div>
        <b-row>
            <b-col md="9" sm="12">
                <div class="row">
                    <div class="col-12">
                        <h2 class="m-0 h1 text-uppercase">{{ $t('partido.elige_asientos') }}</h2>
                        <h3 class="subtitle h3 light mb-3 ">{{ $t('partido.varios_asientos') }}</h3>
                    </div>
                    <b-col cols="12" v-if="zona_seleccionada.with_seats">
                        <TerrenoJuegoComponent />
                        <SelectorAsientosComponent
                            :loading="false"
                            :filas="seat_map"
                            :print_align="zona_seleccionada.print_align"
                            :front="true"
                            @butacaSeleccionada="toggleSeleccion"
                        />
                    </b-col>

                    <b-col cols="12" v-else>
                        <p class="oswald text-uppercase mt-lg-4">
                            {{ $t('partido.numero_entradas', { entradas: manager.termino_entradas }) }}
                        </p>
                        <div class="form-group">
                            <multiselect
                                v-model="n_entradas"
                                :placeholder="$t('partido.numero_entradas', { entradas: manager.termino_entradas })"
                                :show-labels="false"
                                :options="options"
                                :allow-empty="false"
                            />
                        </div>
                    </b-col>
                </div>
            </b-col>
            <b-col md="3" sm="12" class="ml-3 ml-md-0">
                <div class="oswald text-uppercase">{{ $t('partido.zona_seleccionada') }}:</div>
                <span class="h2 mb-1 d-block">
                    {{ zona_seleccionada.name }}
                    <br/>
                    <button
                        v-if="zonas_disponibles.length > 1"
                        class="h6 button small-button m-0"
                        :disabled="false"
                        @click="resetAsientos"
                    >
                        <i class="fa fa-redo"></i> {{ $t('partido.cambiar_zona') }}
                    </button>
                    <button
                        v-if="zona_seleccionada.imagen_panoramica"
                        type="button"
                        class="h6 button small-button m-0 ml-2"
                        @click="$modal.show('imagen-panoramica')"
                    >
                        VER 360º
                    </button>
                </span>
                <ImagenPanoramica
                    v-if="zona_seleccionada.imagen_panoramica"
                    name="imagen-panoramica"
                    :url="zona_seleccionada.imagen_panorama"
                    title="Imagen panorámica de la zona"
                    @close="() => close(false)"
                />
    
                <div v-if="zona_seleccionada.imagen" class="mb-3"><img :src="zona_seleccionada.imagen"/></div>
    
                <label class="mt-3">{{ $t('nuevo_abono.tus_asientos') }}</label>
    
                <div style="max-height: 500px; overflow-y: auto;">
                    <div v-for="(asiento, index) in asientosSeleccionados" :key="index" class="lightgray-content mb-3" :data-testid="'sidebar-asiento'">
                        <div class="text-bold mb-2 d-md-flex p-3">
                            <div class="oswald">
                                {{ (num = index + 1) }}.
                            </div>
                            <div class="ml-2">
                                <small v-if="asiento.asiento.fila">
                                    {{ $t("general.fila") }}: {{ asiento.asiento.fila }}
                                </small>
                                <small v-if="asiento.asiento.asiento">
                                    {{ $t("general.butaca") }}: {{ asiento.asiento.asiento }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
                <b-button class="mb-3" block v-if="asientosSeleccionados.length > 0" @click="continuar()">Continuar</b-button>
            </b-col>
        </b-row>
        <section v-if="reserva.comprar_completa" class="padding-vertical-content lightgray-content">
            <div class="container">
                <div class="row">
                    <div class="col-12">
                        <h1>{{ $t('afiliados.entradas_preseleccionadas') }}</h1>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
    import TerrenoJuegoComponent from '@/components/common/Asientos/TerrenoJuegoComponent.vue';
    import SelectorAsientosComponent from '@/components/common/Asientos/SelectorAsientosComponent.vue';
    import ImagenPanoramica from "@/components/common/ImagenPanoramica";

    // Sentinel predicate: identifies non-numbered "aforo plaza" entries.
    // These arrive as butacas with fila=0 and asiento=0.
    function isSentinelPlaza(butaca) {
        return butaca.fila === 0 && butaca.asiento === 0;
    }

    export default {
        props: {
            reserva: {
                type: Object,
                required: true
            },
            zona_seleccionada: {
                type: Object,
                required: true
            },
            seat_map: {
                type: Object,
                required: true
            },
            asientos: {
                type: Array,
                required: true
            }
        },
        data() {
            return {
                asientosSeleccionados: [...this.asientos],
                n_entradas: 0
            };
        },
        watch: {
            asientos: {
                handler(nuevosAsientos) {
                    this.asientosSeleccionados = [...nuevosAsientos];
                },
                deep: true,
                immediate: true
            },
            n_entradas() {
                // Wrap matches the shape that master getInfoReserva produces for comprar_completa
                // and that the Laravel backend validation expects on guardar-datos.
                if (this.zona_seleccionada && !this.zona_seleccionada.with_seats) {
                    // Non-numbered: aforo plazas arrive as butacas with sentinel fila=0, asiento=0.
                    const plazasAforo = (this.reserva?.butacas ?? []).filter(isSentinelPlaza);
                    this.asientosSeleccionados = this._buildAsientos(plazasAforo);
                } else if (this.n_entradas > 0) {
                    // Numbered: wrap each butaca with {asiento, precios, descuento, precio}.
                    const butacas = this.reserva?.butacas ?? [];
                    this.asientosSeleccionados = this._buildAsientos(butacas);
                } else {
                    this.asientosSeleccionados = [];
                }

                this.$emit('update:asientos', [...this.asientosSeleccionados]);
            }
        },
        methods: {
            wrapAsiento(butaca) {
                return {
                    asiento: butaca,
                    precios: butaca.precios,
                    descuento: this.reserva.descuento,
                    precio: butaca.precio,
                };
            },
            /**
             * Build the wrapped asientos array from a source list, clamping by
             * n_entradas and n_max_asientos. Single source of truth for the
             * wrapping loop shared by the numbered and non-numbered branches
             * of the n_entradas watcher.
             *
             * @param {Array<object>} plazas - source butacas or sentinel plazas
             * @returns {Array<{asiento, precios, descuento, precio}>}
             */
            _buildAsientos(plazas) {
                const maxPlazas = this.reserva?.evento?.n_max_asientos ?? Infinity;
                const cap = Math.min(this.n_entradas, plazas.length, maxPlazas);
                const result = [];
                for (let i = 0; i < cap; i++) {
                    result.push(this.wrapAsiento(plazas[i]));
                }
                return result;
            },
            toggleSeleccion(fila, butaca) {
                let asientoSeleccionado = this.asientosSeleccionados.findIndex(item => item.asiento.asiento === butaca && item.asiento.fila == fila);

                if (asientoSeleccionado !== -1) {
                    this.asientosSeleccionados.splice(asientoSeleccionado, 1);
                } else {
                    if (this.asientosSeleccionados.length >= this.reserva.evento.n_max_asientos) {
                        return this.$toast.error(this.$t('afiliados.limite_asientos', { 'asientos': this.reserva.evento.n_max_asientos }));
                    }

                    let asiento_original = this.reserva.butacas.find(item => item['asiento'] === butaca && item['fila'] == fila && item['zona_id'] == this.zona_seleccionada.id);

                    if (asiento_original) {
                        this.asientosSeleccionados.push({
                            asiento: asiento_original,
                            precios: asiento_original.precios,
                            descuento: this.reserva.descuento,
                            precio: asiento_original.precio
                        });
                    }
                }

                this.seat_map[fila].forEach(asiento => {
                    if (asiento.asiento === butaca && !asiento.ocupado) {
                        asiento.seleccionado = !asiento.seleccionado;
                    }
                });

                this.$emit('update:asientos', [...this.asientosSeleccionados]);
            },
            resetAsientos() {
                if (!this.reserva.comprar_completa) {
                    this.$emit('resetAsientos');
                }
            },
            continuar() {
                this.$emit('asientosSeleccionados');
            }
        },
        computed: {
            zonas_disponibles() {
                return this.reserva.zonas.filter(zona => zona.esta_disponible);
            },
            options() {
                // The multiselect that consumes this computed only renders for
                // non-numbered zones (with_seats=false); the with_seats=true
                // branch was a leftover from a wider contract and is never used.
                if (!this.zona_seleccionada || this.zona_seleccionada.with_seats) {
                    return [];
                }
                // Non-numbered: cap to sentinel plazas count.
                const plazasAforo = (this.reserva?.butacas ?? []).filter(isSentinelPlaza);
                const opciones = [];
                for (let i = 1; i <= plazasAforo.length; i++) {
                    opciones.push(i);
                }
                return opciones;
            },
            manager() {
                return this.$store.getters.getFooter;
            },
            teatro() {
                return this.$store.getters.getTeatro;
            }
        },
        components: {
            TerrenoJuegoComponent, SelectorAsientosComponent, ImagenPanoramica
        }
    }
</script>